import { OrderService } from '@/lib/services/order'
import {
  Contact,
  CreateOrderRequest,
  MasterOrder,
  MasterOrderStatus,
} from '@/types'
import { useRouter } from 'next/router'
import { useState, useEffect, createContext, useMemo, useContext } from 'react'
import { sumOrderAmounts } from '@/helper/utils'
import { ulid } from 'ulid'
import { ExpiredOrderModal } from '@/components/order/expired-order-modal'
import { addMinutes, differenceInSeconds, isFuture } from 'date-fns'
import useAuth from './useAuth'
import { GUEST_JWT_KEY } from '@/lib/constants/auth'
import { useAnalytics } from '@/hooks/use-analytics'
import { EventName } from '@/types/analytics'

interface ICartContextProps {
  masterOrder: MasterOrder | undefined
  isCartLoading: boolean
  addToCart: (orderRequest: CreateOrderRequest) => Promise<MasterOrder>
  clearCart: () => void
  removeOrder: (orderId: number) => void
  markMasterOrderAsExpired: () => void
  updateContacts: (contact: Contact, source: string) => Promise<void>
  totalAmount: number
  idempotency: string
  resetIdempotencyToken: () => void
  refreshCart: () => Promise<void>
  setDrawerOpen: (open: boolean) => void
  isDrawerOpen: boolean
  unlockTimer: string | undefined
}

export const CartContext = createContext<ICartContextProps>(
  {} as ICartContextProps
)

const GUEST_IDEMPOTENCY = 'ORDER_IDEMPOTENCY'
const EXPIRE_MINUTES = 15

const isValidMasterOrder = (masterOrder: MasterOrder) => {
  return (
    masterOrder.status === MasterOrderStatus.Cart &&
    isFuture(addMinutes(masterOrder.createdAt, EXPIRE_MINUTES))
  )
}

export const CartContextProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [masterOrder, setMasterOrder] = useState<MasterOrder>()
  const [isCartLoading, setIsCartLoading] = useState(true)
  const [orderIdempotency, setOrderIdempotency] = useState<string>()
  const [open, setOpen] = useState(false)
  const [isExpiredModalOpen, setIsExpiredModalOpen] = useState(false)
  const router = useRouter()
  const { user } = useAuth()
  const { trackOrder } = useAnalytics()

  const [unlockTimer, setUnlockTimer] = useState<string>()

  useEffect(() => {
    const idempotency = localStorage.getItem(GUEST_IDEMPOTENCY) ?? ulid()

    setOrderIdempotency(idempotency)
    localStorage.setItem(GUEST_IDEMPOTENCY, idempotency)
  }, [])

  useEffect(() => {
    // only fetch cart in app pages
    if (
      !router.pathname.startsWith('/app') &&
      !router.pathname.startsWith('/create-order')
    ) {
      setIsCartLoading(false)
      return
    }

    const initCartItems = async (idempotency: string) => {
      try {
        const masterOrder = await OrderService.getMasterOrder(
          idempotency,
          router.locale
        )

        if (!masterOrder) {
          setMasterOrder(undefined)
          console.info("It's a new order")
          return null
        }

        // Handle empty cart (master order with no orders)
        if (!masterOrder.orders || masterOrder.orders.length === 0) {
          console.warn('Master order has no orders, treating as empty cart')
          setMasterOrder(masterOrder) // Still set it to allow EmptyCartState to render
          return null
        }

        // MasterOrder already placed or if the masterOrder is expired
        if (!isValidMasterOrder(masterOrder)) {
          console.info('Resetting idempotency token')
          resetIdempotencyToken()

          return null
        }

        console.info('Valid master order')
        setMasterOrder(masterOrder)
      } catch (error) {
        console.error('Failed to load master order:', error)
        setMasterOrder(undefined)
      } finally {
        setIsCartLoading(false)
      }
    }

    if (orderIdempotency) {
      setIsCartLoading(true)
      initCartItems(orderIdempotency)
    }
  }, [orderIdempotency, router.pathname, router.locale])

  useEffect(() => {
    if (!masterOrder?.orders?.[0]) {
      return
    }

    function formatSecondsToMinutes(seconds: number) {
      const minutes = Math.floor(seconds / 60) // Calculate whole minutes
      const remainingSeconds = seconds % 60 // Calculate remaining seconds
      return `${String(minutes).padStart(2, '0')}:${String(
        remainingSeconds
      ).padStart(2, '0')}`
    }

    const interval = setInterval(() => {
      const seconds =
        60 * EXPIRE_MINUTES -
        differenceInSeconds(new Date(), new Date(masterOrder?.createdAt))

      if (seconds <= 0) {
        markMasterOrderAsExpired()
      }
      setUnlockTimer(formatSecondsToMinutes(seconds))
    }, 1000)

    return () => clearInterval(interval)
  }, [masterOrder])

  const addToCart = async (
    orderRequest: CreateOrderRequest
  ): Promise<MasterOrder> => {
    let idempotency = orderIdempotency!

    let masterOrder = await OrderService.getMasterOrder(
      idempotency,
      router.locale
    )

    // MasterOrder already placed or if the masterOrder is expired
    if (masterOrder && !isValidMasterOrder(masterOrder)) {
      idempotency = ulid()
      setOrderIdempotency(idempotency)
      localStorage.setItem(GUEST_IDEMPOTENCY, idempotency)
    }

    // Add order to the masterOrder
    masterOrder = await OrderService.createOrder(idempotency, {
      creatorId: user?.id,
      ...orderRequest,
    })

    setMasterOrder(masterOrder)

    // Track add to cart event
    if (masterOrder && masterOrder.orders?.length > 0) {
      const newOrder = masterOrder.orders[masterOrder.orders.length - 1]
      trackOrder(EventName.CartAdd, {
        orderId: masterOrder.id.toString(),
        experienceId: newOrder.experience?.id?.toString(),
        experienceName: newOrder.experienceName,
        quantity: newOrder.quantity,
        amount: newOrder.amount,
        currency: 'EUR',
      })
    }

    return masterOrder
  }

  const removeOrder = async (orderId: number) => {
    // Find the order being removed for tracking
    const removedOrder = masterOrder?.orders?.find(
      (order) => order.id === orderId
    )

    try {
      await OrderService.removeOrder(orderIdempotency!, orderId)
    } catch (e) {}

    setMasterOrder((prev) => {
      if (!prev) return prev
      const remainingOrders = (prev.orders ?? []).filter(
        (order) => order.id !== orderId
      )
      return {
        ...prev,
        orders: remainingOrders,
        amount: sumOrderAmounts(remainingOrders),
      } as MasterOrder
    })

    // Track remove from cart event
    if (removedOrder) {
      trackOrder(EventName.CartRemove, {
        orderId: orderId.toString(),
        experienceId: removedOrder.experience?.id?.toString(),
        experienceName: removedOrder.experience?.name,
        amount: removedOrder.amount,
        currency: 'EUR',
      })
    }
  }

  const markMasterOrderAsExpired = async () => {
    try {
      await OrderService.markMasterOrderAsExpired(masterOrder!.id)
      setIsExpiredModalOpen(true)

      // Track cart expiration event
      if (masterOrder) {
        trackOrder(EventName.CartExpire, {
          orderId: masterOrder.id.toString(),
          amount: totalAmount,
          currency: 'EUR',
        })
      }
    } catch (e) {}
    setMasterOrder({} as MasterOrder)
  }

  const updateContacts = async (contact: Contact, source: string) => {
    setMasterOrder({ ...masterOrder, customer: contact } as MasterOrder)

    const jwtToken = localStorage.getItem(GUEST_JWT_KEY) ?? undefined
    await OrderService.updateContacts(masterOrder!.id, contact, source, jwtToken)
  }

  const totalAmount = useMemo(
    () => sumOrderAmounts(masterOrder?.orders),
    [masterOrder]
  )

  const clearCart = () => {}

  const refreshCart = async () => {
    if (!orderIdempotency) return
    try {
      const updated = await OrderService.getMasterOrder(
        orderIdempotency,
        router.locale
      )
      if (updated && isValidMasterOrder(updated)) {
        setMasterOrder(updated)
      }
    } catch (error) {
      console.error('Failed to refresh cart:', error)
    }
  }

  const resetIdempotencyToken = () => {
    const idempotency = ulid()
    localStorage.setItem(GUEST_IDEMPOTENCY, idempotency)
    setOrderIdempotency(idempotency)

    return idempotency
  }

  return (
    <CartContext.Provider
      value={{
        masterOrder,
        isCartLoading,
        addToCart,
        markMasterOrderAsExpired,
        removeOrder,
        clearCart,
        totalAmount,
        updateContacts,
        idempotency: orderIdempotency ?? '',
        refreshCart,
        resetIdempotencyToken,
        setDrawerOpen: (open: boolean) => setOpen(open),
        isDrawerOpen: open,
        unlockTimer,
      }}
    >
      {children}
      <ExpiredOrderModal
        isOpen={isExpiredModalOpen}
        onClose={() => setIsExpiredModalOpen(false)}
      />
    </CartContext.Provider>
  )
}

const useCart = () => useContext(CartContext)

export default useCart
